import { v4 as uuid } from "uuid";
import { captureException } from "@sentry/react";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavigateFunction, Outlet, useNavigate } from "react-router-dom";
import { filter } from "rxjs/operators";
import { BankingWebCardService } from "/src/protos/squareup/bbfrontend/bankingweb/v1/card_service";
import { BankingWebComplianceService } from "/src/protos/squareup/bbfrontend/bankingweb/v1/compliance_service";
import {
  BankingWebService,
  ListOpenCardInvitationsRequest,
} from "/src/protos/squareup/bbfrontend/bankingweb/v1/service";
import {
  GetPersonProfileRequest,
  GetPersonProfileResponse,
  PersonProfileService,
} from "/src/protos/squareup/bbfrontend/bankingweb/v1/person_profile_service";
import { WebService } from "/src/protos/squareup/bbfrontend/v1/web_service";
import { OpenCardInvitation } from "/src/protos/squareup/bbfrontend/bankingweb/v1/model";
import { initializeWorkflow } from "/src/lib/workflow-react/use-workflow";
import * as cardIssuing from "/src/workflow/card-issuing";
import cardIssuingAnalytics from "/src/workflow/card-issuing/analytics";
import { Page } from "/src/components/ui/page/Page";
import assert from "/src/util/assert";
import TextRenderer from "/src/util/text-renderer";
import { createUserFromProfile } from "/src/util/user";
import { ConnectedCardIssuingModal } from "/src/components/card-issuing/connected/ConnectedCardIssuingModal";
import { useCdp } from "/src/hooks/use-cdp";
import { useUser } from "/src/hooks/use-user";
import { useWindowDimensions } from "/src/hooks/use-window-dimensions";
import { CardList, CardListItemProps } from "/src/components/card-home/CardList";
import { ActionCardListItemProps } from "/src/components/card-home/ActionCardListItem";
import * as styles from "./CardHome.css";
import {
  createActivatableCardList,
  createCardList,
  sortCards,
} from "/src/pages/card-home/card-home";
import AlertNoEntry from "tsx:/public/assets/svg/market-icons/alert-no-entry.svg";
import {
  CardManagementService,
  ListCardsRequest,
} from "/src/protos/squareup/bbdebit/cardmanagement";
import { ExternalBizbankService } from "/src/protos/squareup/bizbank/externalservice";
import { UserCardInvitationService } from "/src/protos/squareup/banking/card_invitation";
import { GetCardCustomizationRequest } from "/src/protos/squareup/bizbank/service";
import { TFunction } from "i18next";
import { CardCustomization } from "/src/protos/squareup/bizbank/service";

export interface CardHomeProps {
  readonly bankingWebCardService: BankingWebCardService;
  readonly bankingWebComplianceService: BankingWebComplianceService;
  readonly bankingWebService: BankingWebService;
  readonly cardManagementService: CardManagementService;
  readonly externalBizbankService: ExternalBizbankService;
  readonly personProfileService: PersonProfileService;
  readonly userCardInvitationService: UserCardInvitationService;
  readonly webService: WebService;
}

async function fetchCardsAndInvitations(
  cardManagementService: CardManagementService,
  bankingWebService: BankingWebService,
  externalBizbankService: ExternalBizbankService,
  t: TFunction,
  navigate: NavigateFunction
) {
  // Fetch all Cards and OpenCardInvitations
  const [cardsResponse, invitationsResponse] = await Promise.all([
    cardManagementService.listCards(ListCardsRequest.create()),
    bankingWebService.listOpenCardInvitations(ListOpenCardInvitationsRequest.create()),
  ]);
  const cards = [...(cardsResponse.cards ?? [])];

  // Fetch the CardCustomization, which contains the Signature, for each Card
  // Put the CardCustomizations in a Map for easy access when rendering the cards
  const customizationEntries = await Promise.all(
    cards.map(async (card): Promise<[string, CardCustomization | null]> => {
      const token = card.fulfillment?.cardCustomization?.id;
      if (!token) return [card.id, null];

      const res = await externalBizbankService.getCardCustomization(
        GetCardCustomizationRequest.create({ customizationToken: token })
      );
      return [card.id, res.cardCustomization ?? null];
    })
  );
  const customizationMap = new Map<string, CardCustomization | null>(customizationEntries);

  // Separate ActivatableCards from ClaimedCards
  // Create lists of ActionCardListItemProps and CardListItemProps for display
  const [activatableCards, claimedCards] = sortCards(cards);
  const activatableCardsListItems = createActivatableCardList(t)(
    activatableCards,
    customizationMap,
    (card) => navigate(`/activate-card/${card.id}`)
  );
  const cardsListItems = createCardList(navigate, t)(claimedCards, customizationMap);

  return {
    activatableCardsListItems,
    cardsListItems,
    cardInvitations: [...(invitationsResponse.openCardInvitations ?? [])],
  };
}

export function CardHome({
  bankingWebCardService,
  bankingWebComplianceService,
  bankingWebService,
  cardManagementService,
  externalBizbankService,
  personProfileService,
  userCardInvitationService,
  webService,
}: CardHomeProps) {
  const cdp = useCdp();
  const { t } = useTranslation("common");
  const [user, setUser] = useUser();
  const navigate = useNavigate();
  const { isMobile } = useWindowDimensions();

  const [loading, setLoading] = useState(true);
  const [cardInvitations, setCardInvitations] = useState<OpenCardInvitation[]>([]);
  const [activatableCardsListItems, setActivatableCardsListItems] = useState<
    ActionCardListItemProps[]
  >([]);
  const [cardsListItems, setCardsListItems] = useState<CardListItemProps[]>([]);

  const [cardIssuingState, cardIssuingDispatch, cardIssuingWorkflow] = initializeWorkflow(
    {
      bankingWebCardService,
      bankingWebComplianceService,
      cardManagementService,
      personProfileService,
      sentry: { captureException },
      textRenderer: TextRenderer,
      userCardInvitationService,
      uuid,
      webService,
    },
    cardIssuing.createState(),
    cardIssuing.default
  );

  useEffect(() => {
    // Initialize the workflow
    cardIssuingWorkflow.states
      .pipe(filter(({ done }: cardIssuing.State) => done))
      .subscribe((state: cardIssuing.State) => {
        personProfileService
          .getPersonProfile(GetPersonProfileRequest.create())
          // TODO(cvu): Figure out how to move the responsibility of re-fetching person data into UserContext
          // Going to leave this for now. Tech debt ticket: https://jira.sqprod.co/browse/SQCARD-2840
          .then((response: GetPersonProfileResponse) => {
            const profile = response.profile;
            assert(profile, "Profile does not exist in GetPersonProfileResponse");
            setUser(createUserFromProfile(profile));
            navigate(`/card/${state.cardToken}/settings`);
          });
      });

    // Initialize analytics
    cardIssuingAnalytics(cardIssuingWorkflow.updates).subscribe((params) => {
      cdp.trackV2(params);
    });

    // Fetch all Cards and Invitations
    const getCards = async () => {
      const { activatableCardsListItems, cardsListItems, cardInvitations } =
        await fetchCardsAndInvitations(
          cardManagementService,
          bankingWebService,
          externalBizbankService,
          t,
          navigate
        );

      setActivatableCardsListItems(activatableCardsListItems);
      setCardsListItems(cardsListItems);
      setCardInvitations(cardInvitations);
      setLoading(false);
    };
    getCards();
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    // Open ordering flow if the person only has one invitation and no cards.
    if (
      cardInvitations.length === 1 &&
      activatableCardsListItems.length === 0 &&
      cardsListItems.length === 0
    ) {
      cardIssuingDispatch(
        cardIssuing.initializeAction(
          cardInvitations[0].balanceId,
          cardInvitations[0].name,
          cardInvitations[0].cardInvitationToken,
          user || { personToken: "", name: { first: "", last: "" } },
          true
        )
      );
      cardIssuingDispatch(cardIssuing.toggleModalAction(true));
    }
  }, [loading]);

  const cardInvitationListItems = cardInvitations.map(
    (cardInvitation): ActionCardListItemProps => ({
      buttonText: t("cardList.newCard.orderCard.buttonText"),
      cardToken: cardInvitation.cardInvitationToken,
      subtext: t("cardList.newCard.subtext"),
      title: cardInvitation.name,
      onItemClick: () => {
        cardIssuingDispatch(
          cardIssuing.initializeAction(
            cardInvitation.balanceId,
            cardInvitation.name,
            cardInvitation.cardInvitationToken,
            user || { personToken: "", name: { first: "", last: "" } },
            cardsListItems.length === 0
          )
        );
        cardIssuingDispatch(cardIssuing.toggleModalAction(true));
      },
    })
  );

  const allListItems = [
    ...cardInvitationListItems,
    ...activatableCardsListItems,
    ...cardsListItems,
  ];
  const hasListItems = allListItems.length > 0;

  // Render content based on the state of the Card lists
  const renderContent = () => {
    if (!hasListItems) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.iconContainer}>
            <AlertNoEntry className={styles.icon} />
          </div>
          <p>{t("cardHome.emptyState")}</p>
        </div>
      );
    } else if (isMobile) {
      return <CardList cardListItems={allListItems} />;
    } else {
      const actionableItems = [...cardInvitationListItems, ...activatableCardsListItems];
      return (
        <div className={styles.desktopCardLists}>
          {actionableItems.length > 0 && <CardList cardListItems={actionableItems} />}
          {cardsListItems.length > 0 && <CardList cardListItems={cardsListItems} />}
        </div>
      );
    }
  };

  return (
    <>
      <Page loading={loading} className={styles.page}>
        <h2 className={classNames("header-30", styles.header)}>{t("cardHome.list.header")}</h2>
        {renderContent()}
        <Outlet />
      </Page>
      <ConnectedCardIssuingModal state={cardIssuingState} dispatch={cardIssuingDispatch} />
    </>
  );
}
